import { ApolloCache, gql } from '@apollo/client';
import dayjs from 'dayjs';
import { dateSorterGeneric, getToday } from '../../helpers/datetime';
import { achievementFragment } from '../fragments/achievement';
import { questFragment } from '../fragments/quest';
import {
    CompleteQuestMutation,
    GetLatestAchievementsQuery,
    GetLatestAchievementsQueryVariables,
    GetQuestsQuery,
    GetQuestsQueryVariables,
    GetRangeHistoryQuery,
    GetRangeHistoryQueryVariables,
} from '../generated/graphql';
import { getLatestAchievements } from '../queries/achievement';
import { getQuestsQuery } from '../queries/quest';
import { getRangeHistoryQuery } from '../queries/rangeHistory';
import { forEachPrimaryQuestsQuery, getUserDataFromCache } from './cacheHelpers';

const LATEST_ACHIEVEMENTS_LIMIT = 3;

export const completeQuestMutation = gql`
    ${questFragment}
    ${achievementFragment}

    mutation CompleteQuest($questId: String!, $dueDate: DateTimeISO, $timerValueInMs: Int, $timeOfDay: TimeOfDay) {
        completeQuest(data: { id: $questId, dueDate: $dueDate, timerValueInMs: $timerValueInMs, timeOfDay: $timeOfDay }) {
            quest {
                ...QuestFields
            }
            completedXp
            completedAchievements {
                ...AchievementFields
            }
            progressedAchievements {
                id
                name
                progress
                progressText
            }
            goal {
                id
                completedXP
            }
            eventLog {
                id
                createdAt
                timeOfDay
                eventType
                questCompleted {
                    questId
                }
                questMetricIncreased {
                    questId
                    addedValue
                    questMetricType
                }
            }
        }
    }
`;

export function completeQuestCache(cache: ApolloCache<any>, { data }: { data?: CompleteQuestMutation | null }) {
    if (!data?.completeQuest) return;

    const completedQuest = data.completeQuest.quest;
    const completedQuestGoal = data.completeQuest.goal;

    // Update category questProgressCompleted in cache
    if (completedQuest.categoryId) {
        cache.modify({
            id: cache.identify({ __typename: 'Category', id: completedQuest.categoryId }),
            fields: {
                questProgressCompleted(existingCompleted = 0) {
                    return existingCompleted + 1;
                },
            },
        });
    }

    // Update all primary queries (those with dueDateFrom: null)
    forEachPrimaryQuestsQuery(cache, (vars, existingData) => {
        const updatedQuests = existingData.quests.map(q => {
            if (q.id === completedQuest.id) {
                // Merge the completed quest data
                return { ...q, ...completedQuest };
            }
            return q;
        });

        // Update goal completedXP if this query includes goals
        const updatedGoals = existingData.goals.map(g => {
            if (g.id === completedQuestGoal?.id) {
                return { ...g, completedXP: completedQuestGoal.completedXP };
            }
            return g;
        });

        cache.writeQuery<GetQuestsQuery, GetQuestsQueryVariables>({
            query: getQuestsQuery,
            variables: vars,
            data: { ...existingData, quests: updatedQuests, goals: updatedGoals },
        });
    });

    // Get user data for event log updates from any primary cached query
    const userData = getUserDataFromCache(cache);

    const completedAchievements = data?.completeQuest?.completedAchievements ?? [];
    if (completedAchievements.length > 0) {
        // Update category achievementProgressCompleted for each completed achievement
        completedAchievements.forEach(completedAchievement => {
            // Get the achievement from cache to access categoryId
            try {
                const cachedAchievement = cache.readFragment<{ categoryId: string }>({
                    id: cache.identify({ __typename: 'Achievement', id: completedAchievement.id }),
                    fragment: gql`
                        fragment AchievementCategory on Achievement {
                            categoryId
                        }
                    `,
                });

                if (cachedAchievement?.categoryId) {
                    cache.modify({
                        id: cache.identify({ __typename: 'Category', id: cachedAchievement.categoryId }),
                        fields: {
                            achievementProgressCompleted(existingCompleted = 0) {
                                return existingCompleted + 1;
                            },
                        },
                    });
                }
            } catch {
                // Achievement not in cache, skip category update
            }
        });

        const latestData = cache.readQuery<GetLatestAchievementsQuery, GetLatestAchievementsQueryVariables>({
            query: getLatestAchievements,
            variables: { limit: LATEST_ACHIEVEMENTS_LIMIT, orderBy: 'achievedOn' },
        });

        if (latestData?.achievements) {
            const existingAchievementsMap = new Map(latestData.achievements.map(a => [a.id, a]));

            completedAchievements.forEach(completedAchievement => {
                existingAchievementsMap.set(completedAchievement.id, completedAchievement);
            });

            const allAchievements = Array.from(existingAchievementsMap.values()).sort((a, b) =>
                dateSorterGeneric(a, b, i => i.achievedOn || '', false),
            );

            const updatedLatestAchievements = allAchievements.slice(0, LATEST_ACHIEVEMENTS_LIMIT);

            cache.writeQuery<GetLatestAchievementsQuery, GetLatestAchievementsQueryVariables>({
                query: getLatestAchievements,
                variables: { limit: LATEST_ACHIEVEMENTS_LIMIT, orderBy: 'achievedOn' },
                data: { ...latestData, achievements: updatedLatestAchievements },
            });
        }
    }

    const eventLog = data?.completeQuest?.eventLog!;

    if (eventLog && userData?.goalCompletion) {
        const end = dayjs(userData.goalCompletion);
        const start = end.subtract(1, 'week');
        const createdAt = dayjs(eventLog.createdAt);

        if (createdAt.isAfter(start) && createdAt.isBefore(end)) {
            const oldRecordsData = cache.readQuery<GetRangeHistoryQuery, GetRangeHistoryQueryVariables>({
                query: getRangeHistoryQuery,
                variables: { start, end },
            });
            const newRecords = oldRecordsData?.eventLogs ? [...oldRecordsData.eventLogs, eventLog] : [eventLog];
            cache.writeQuery<GetRangeHistoryQuery, GetRangeHistoryQueryVariables>({
                query: getRangeHistoryQuery,
                variables: { start, end },
                data: { ...oldRecordsData!, eventLogs: newRecords as any },
            });
        }
    }

    if (eventLog) {
        const today = getToday();
        const endOfToday = today.add(1, 'day');
        const createdAt = dayjs(eventLog.createdAt);
        if (createdAt.isBefore(today) || createdAt.isAfter(endOfToday)) return;

        const start = today;
        const end = start.add(1, 'day');

        const oldRecordsData = cache.readQuery<GetRangeHistoryQuery, GetRangeHistoryQueryVariables>({
            query: getRangeHistoryQuery,
            variables: { start, end },
        });
        if (oldRecordsData?.eventLogs) {
            const newRecords = oldRecordsData?.eventLogs ? [...oldRecordsData.eventLogs, eventLog] : [eventLog];
            cache.writeQuery<GetRangeHistoryQuery, GetRangeHistoryQueryVariables>({
                query: getRangeHistoryQuery,
                variables: { start, end },
                data: { ...oldRecordsData!, eventLogs: newRecords as any },
            });
        }
    }
}
